import { FC, memo } from 'react';
import { EdnaWrapper } from '@providers/EdnaProvider';
import styled, { css } from 'styled-components';

import Tooltip from '@components/Tooltip';
import Text from '@components/Text';
import { ImageStackProps, ImageStackSizeType } from './types';

export const FALLBACK_IMAGE =
  'https://g-ecx.images-amazon.com/images/G/01/x-site/icons/no-img-sm._CB192198896_.gif';

const SIZE_VARIANTS: Record<ImageStackSizeType, number> = {
  small: 42,
  medium: 60,
  large: 90,
  xlarge: 136,
};

const Wrapper = styled.div<{ $size: number; $margin: string }>`
  position: relative;
  height: ${(props) => props.$size}px;
  width: ${(props) => props.$size}px;
  min-height: ${(props) => props.$size}px;
  min-width: ${(props) => props.$size}px;
  margin: ${(props) => props.$margin};
`;

const Image = styled.img<{
  $size: number;
  $showStack: boolean;
  $padding: string | number;
}>`
  display: grid;
  background-color: white;
  border: 1px solid ${(props) => props.theme.colors.grey100};
  padding: ${(props) => props.$padding};
  border-radius: 4px;
  object-fit: contain;
  box-sizing: border-box;
  height: ${(props) => props.$size}px;
  width: ${(props) => props.$size}px;
  min-height: ${(props) => props.$size}px;
  min-width: ${(props) => props.$size}px;
  ${(props) =>
    props.$showStack &&
    css`
      position: absolute;
      &:first-child {
        right: 0;
        bottom: 0;
      }
      &:last-child {
        top: 0;
        left: 0;
      }
    `}
`;

const TooltipImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 180px;
    height: 180px;
    object-fit: contain;
  }
`;

const Badge = styled.div<{ $showStack: boolean }>`
  display: flex;
  box-sizing: border-box;
  background-color: ${(props) => props.theme.colors.grey075};
  border-radius: 2px;
  width: fit-content;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  justify-self: flex-end;
  position: absolute;
  top: 2px;
  right: ${(props) => (props.$showStack ? '6px' : '2px')};
`;

/**
 * A component that displays an image or a stack of images with an optional badge number.
 * The image can be displayed as a stack of images, often used to indicate multiples of something like in the case of product variants.
 * The image can also display a tooltip when hovering over the image to show a larger version of the image.
 */
const ImageStack: FC<ImageStackProps> = ({
  src,
  largeSrc,
  alt,
  size = 'medium',
  margin = '0',
  showOnHover = true,
  showStack = false,
  className,
  badgeNumber,
}) => {
  const wrapperSize = SIZE_VARIANTS[size];
  const imageSize = showStack ? wrapperSize - 4 : wrapperSize;

  return (
    <EdnaWrapper>
      <Tooltip
        content={
          <TooltipImage>
            <img
              src={largeSrc || src}
              alt={alt}
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMAGE;
              }}
            />
          </TooltipImage>
        }
        disabled={!showOnHover}
        side="bottom"
        align="start"
        backgroundColor="white"
      >
        <Wrapper $size={wrapperSize} $margin={margin} className={className}>
          {showStack && (
            <Image
              as="div"
              role="presentation"
              $size={imageSize}
              $showStack={showStack}
              $padding={0}
            />
          )}
          <Image
            src={src}
            $size={imageSize}
            $showStack={showStack}
            $padding={size === 'small' ? '2px' : '4px'}
            alt={alt}
            onError={(e) => {
              e.currentTarget.src = FALLBACK_IMAGE;
            }}
          />
          {badgeNumber && (
            <Badge $showStack={showStack} aria-label="Number of items">
              <Text element="span" variant="bodyXs">
                {badgeNumber}
              </Text>
            </Badge>
          )}
        </Wrapper>
      </Tooltip>
    </EdnaWrapper>
  );
};

ImageStack.displayName = 'ImageStack';

// Export for Storybook-Only, not exposed elsewhere. Memoized components can't show descriptions or default values.
export { ImageStack };

export default memo(ImageStack);
